import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { motion } from "framer-motion";

const isTelegram =
  typeof window !== "undefined" &&
  window.Telegram &&
  window.Telegram.WebApp;

// ── CONTRACT ADDRESSES & ABIs ─────────────────────────────────────
const CONTRACT_ADDRESS = "0x01D675A61d94aEa570007374d85301d6619D9E6e";
// DOGE_ADDRESS removed – native DOGE is used via provider.getBalance()
const MCRIB_ADDRESS = "0xbdaD927604c5cB78F15b3669a92Fa5A1427d33a2";

// MULTICALL — Dogechain (correct address)
const MULTICALL_ADDRESS = "0xcA11bde05977b3631167028862bE2a173976CA11";

const MULTICALL_ABI = [
  "function aggregate(tuple(address target, bytes callData)[] calls) public view returns (uint256 blockNumber, bytes[] returnData)"
];

const ABI = [
  "event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)",
  // PAYABLE mint (so we can send DOGE as msg.value)
  "function mint(uint256 num) external payable",
  // Dynamic pricing / reveal config
  "function dogeFee() external view returns (uint256)",
  "function mcribPrice() external view returns (uint256)",
  "function revealBlock() external view returns (uint256)",
  "function revealed() external view returns (bool)",
  // Core ERC721-ish stuff
  "function tokenURI(uint256 id) external view returns (string)",
  "function totalSupply() external view returns (uint256)",
  "function ownerOf(uint256 tokenId) external view returns (address)",
  "function balanceOf(address owner) external view returns (uint256)",
  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  // NEW ERC721Enumerable FUNCTIONS — FOR RANDOM ID TRACKING
  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  "function tokenOfOwnerByIndex(address owner, uint256 index) external view returns (uint256)",
  "function tokenByIndex(uint256 index) external view returns (uint256)",
  "function isEaten(uint256) external view returns (bool)",
  "function tokenBalance(uint256 id) external view returns (uint256)",
  "function eatMcRib(uint256 id) external"
];

const ERC20_ABI = [
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function balanceOf(address account) external view returns (uint256)"
];

const TX_OPTIONS = {
  gasPrice: ethers.utils.parseUnits("250", "gwei"),
  // gasLimit removed → now calculated dynamically inside handleMint
};

const DOGECHAIN = {
  chainId: "0x7D0",
  chainName: "Dogechain",
  rpcUrls: ["https://rpc.dogechain.dog"],
  nativeCurrency: { name: "DOGE", symbol: "DOGE", decimals: 18 },
  blockExplorerUrls: ["https://explorer.dogechain.dog"]
};

// MCRIB_REQUIRED removed – using contract.mcribPrice() dynamically
 // 50B × 10¹⁵ (not used now; left in for reference)
// DOGE_REQUIRED removed – using contract.dogeFee() dynamically
 // not used now; dynamic from contract

const TRAIT_RARITY = {
  buns: { "Standard": 40, "Toasted": 35, "Extra Crispy": 25 },
  patties: { "Classic": 30, "Smoky": 30, "Double Stack": 25, "Charred": 15 },
  sauces: { "BBQ Drip": 40, "Spicy Glaze": 35, "Sweet Heat": 25 },
  onions: { "None": 20, "Light": 25, "Heavy": 20, "Crispy": 20, "Caramelized": 15 },
  pickles: { "None": 30, "Single": 30, "Double": 25, "Triple": 15 },
  topBuns: { "Sesame": 60, "Plain": 40 },
  bottomBun: {
    "Standard Bottom": 67,
    "Toasted Edge": 20,
    "Buttered Glow": 10,
    "Charred Perfection": 3 // ← 3% CHANCE — LEGENDARY
  }
};

let provider, signer, contract;

// Browser / MetaMask ONLY
if (!isTelegram && typeof window !== "undefined" && window.ethereum) {
  provider = new ethers.providers.Web3Provider(window.ethereum);
  signer = provider.getSigner();
  contract = new ethers.Contract(CONTRACT_ADDRESS, ABI, signer);
}

export default function App() {
  const NFTS_PER_PAGE = 25; // Constant for pagination limit

  // ---- CACHED MINT EVENTS FOR LAST 24 HOURS ----
  const [cachedMints, setCachedMints] = useState([]);
  
  // Quick helper: returns last 24 hours from the cache
  const getRecentMintsFromCache = () => {
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  return cachedMints.filter(m => m.timestamp >= cutoff);
};
  
  const [account, setAccount] = useState("");
  const [recentMints, setRecentMints] = useState([]);
  const [myNFTs, setMyNFTs] = useState([]);
  const [mintedCount, setMintedCount] = useState(0);

  //
  // REALTIME TOTAL SUPPLY LOADER — REPLACES ALL OLD loadData() SUPPLY LOGIC
  //
  const loadTotalSupplyRealtime = async () => {
    try {
      console.log("[SUPPLY] Fetching totalSupply() from contract...");
      if (!contract) return;

      const supplyBN = await contract.totalSupply();
      const supply = supplyBN.toNumber();

      console.log("[SUPPLY] Realtime supply:", supply);
      setMintedCount(supply);
    } catch (err) {
      console.error("[SUPPLY] ERROR:", err);
    }
};

// =========================================================
// LOAD MINT COUNT — (NO INTERVALS, NO SPAM, JUST ON-DEMAND)
// =========================================================
const loadMintCount = async () => {
  try {
    if (!contract) return;

    console.log("[SUPPLY] Loading totalSupply()…");
    const supplyBN = await contract.totalSupply();
    const supply = supplyBN.toNumber();

    setMintedCount(supply);
    console.log("[SUPPLY] Updated mint count:", supply);
  } catch (err) {
    console.error("[SUPPLY] Failed to load mint count:", err);
  }
};

  const [cooking, setCooking] = useState(false);
  const [mintStep, setMintStep] = useState("");
  const [selectedNFT, setSelectedNFT] = useState(null);
  const [activeTab, setActiveTab] = useState("recent");
  const [userMcribBalance, setUserMcribBalance] = useState("0");
  const [userDogeBalance, setUserDogeBalance] = useState("0"); // ← NATIVE DOGE (MetaMask's DOGE)
  const [isEating, setIsEating] = useState(false);
  const [mcribContract, setMcribContract] = useState(null);
  const [allTokenIDs, setAllTokenIDs] = useState([]);
  const [tokenIDCache, setTokenIDCache] = useState({}); // NEW: Multi-wallet cache { 'addr': [ids] }
  const [isTokenListLoading, setIsTokenListLoading] = useState(false); // NEW: Background loading state // All IDs owned by user
  const [currentPage, setCurrentPage] = useState(1); // Current pagination page

  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  // DYNAMIC PRICING FROM CONTRACT (LIVE)
  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  const [dogeFee, setDogeFee] = useState(null);
  const [mcribPrice, setMcribPrice] = useState(null);

  const BACKGROUND_RARITIES = {
    "Off-White Background": "89.5%",
    "Classic McDonalds Wrapper": "1.0%",
    "Red Arches Tray": "2.0%",
    "Gray/White Tray": "3.0%",
    "Night sky vibes": "4.0%",
    "Golden Tray": "0.5%",
  };

  useEffect(() => {
    if (provider && account) {
      const signerInstance = provider.getSigner();
      setMcribContract(new ethers.Contract(MCRIB_ADDRESS, ERC20_ABI, signerInstance));
    }
  }, [provider, account]);

  // ── LOAD LIVE PRICES FROM CONTRACT ─────────────────────────────
  useEffect(() => {
    if (contract) {
      Promise.all([contract.dogeFee(), contract.mcribPrice()])
        .then(([doge, mcrib]) => {
          setDogeFee(doge);
          setMcribPrice(mcrib);
        })
        .catch(console.error);
    }
  }, [contract]);

  const handleEatMcRib = async (tokenId) => {
    if (!selectedNFT || !mcribContract || !contract) return;

    setIsEating(true);
    try {
      const signerInstance = provider.getSigner();
      const eatContract = new ethers.Contract(CONTRACT_ADDRESS, ABI, signerInstance);

      const requiredTokens = await eatContract.tokenBalance(tokenId);
      if (requiredTokens.isZero()) throw new Error("This McRib has no tokens remaining!");

      const allowance = await mcribContract.allowance(account, CONTRACT_ADDRESS);

      if (allowance.lt(requiredTokens)) {
        alert("Approving MCRIB tokens for the contract...");
        let txn = await mcribContract.approve(CONTRACT_ADDRESS, requiredTokens.mul(10));
        await txn.wait();
      }

      alert(`Executing eatMcRib for token #${tokenId}...`);
      let txn = await eatContract.eatMcRib(tokenId);
      await txn.wait();  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
                         // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←

      alert(`McRib #${tokenId} has been successfully eaten!`);

      // CRITICAL FIX: Refresh all states and the expanded card after transaction confirmation.
      loadBalances();
      await loadOwnedTokenIDs();

      const eatenTokenId = selectedNFT.id;
      const freshNFTMetadata = await fetchAndCacheMetadata(eatenTokenId, contract, false);
      if (freshNFTMetadata) {
        setSelectedNFT(freshNFTMetadata);
      }

      await loadMyMcRibsFast(currentPage); // ← NOW USING FAST VERSION

    } catch (error) {
      console.error("Error eating McRib:", error);
      alert(`Failed to eat McRib: ${error.reason || error.message || "Transaction failed."}`);
    } finally {
      setIsEating(false);
    }
  };

  // ————————————————————————————————————
  // ALL FUNCTIONS — IN CORRECT ORDER TO AVOID "before initialization" ERRORS
  // ————————————————————————————————————
  const switchToDogechain = async () => {
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: DOGECHAIN.chainId }]
      });
    } catch (e) {
      if (e.code === 4902) {
        await window.ethereum.request({
          method: "wallet_addEthereumChain",
          params: [DOGECHAIN]
        });
      }
    }
  };

const connectWallet = async () => {
  try {
    // 🟦 TELEGRAM PATH
    if (isTelegram) {
      const tg = window.Telegram.WebApp;
      tg.ready();

      // 🔑 You already pass this from your bot
      // Example: wallet address attached to Telegram user
      const walletFromBot = tg.initDataUnsafe?.user?.wallet;

      if (!walletFromBot) {
        alert("No wallet linked to this Telegram account.");
        return;
      }

      setAccount(walletFromBot);

      // IMPORTANT: provider should be JSON-RPC, not MetaMask
	provider = new ethers.providers.JsonRpcProvider("https://rpc.dogechain.dog");
	signer = null; // IMPORTANT: no signing in Mini App
contract = new ethers.Contract(CONTRACT_ADDRESS, ABI, provider);

      await loadMyMcRibsFast(currentPage);
      await loadBalances();
      return;
    }

    // 🟩 NORMAL BROWSER / METAMASK PATH
await switchToDogechain();

if (!window.ethereum) {
  alert("MetaMask not available");
  return;
}

const web3Provider = new ethers.providers.Web3Provider(window.ethereum, "any");
await web3Provider.send("eth_requestAccounts", []);

const signerInstance = web3Provider.getSigner();
const address = await signerInstance.getAddress();

provider = web3Provider;     // overwrite safely
signer = signerInstance;
contract = new ethers.Contract(CONTRACT_ADDRESS, ABI, signer);

setAccount(address);

await loadMyMcRibsFast(currentPage);
await loadBalances();

    await loadMyMcRibsFast(currentPage);
    await loadBalances();

  } catch (e) {
    alert("Wallet connection failed: " + e.message);
  }
};

  const disconnectWallet = () => {
    setAccount("");
    setMyNFTs([]);
    setSelectedNFT(null);
    setUserMcribBalance("0");
    setUserDogeBalance("0");
    setAllTokenIDs([]);
  };

  const loadBalances = async () => {
    if (!account) return;
    try {
      const mcrib = new ethers.Contract(MCRIB_ADDRESS, ERC20_ABI, provider);
      const nativeDogeBal = await provider.getBalance(account);
      const mcribBalRaw = await mcrib.balanceOf(account);
      setUserMcribBalance(ethers.utils.formatUnits(mcribBalRaw, 15));
      setUserDogeBalance(ethers.utils.formatEther(nativeDogeBal));
    } catch (e) {
      console.error("Balance load failed", e);
    }
  };

// =========================================================
// REAL-TIME EVENT-DRIVEN “FRESH OFF THE GRILL” SYSTEM
// =========================================================

// Local cache key
const RECENT_MINTS_KEY = "mcrib_recent_mints";

// Load from cache on startup
const loadRecentMintsFromCache = () => {
  try {
    const raw = localStorage.getItem(RECENT_MINTS_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
};

// Save back to cache
const saveRecentMintsToCache = (list) => {
  localStorage.setItem(RECENT_MINTS_KEY, JSON.stringify(list));
};

// =========================================================
// MAIN MINT HANDLER (ONE mint → ONE update, ZERO SPAM)
// =========================================================
const handleMintEvent = async (tokenId) => {
  const idStr = tokenId.toString();
  console.log("[FRESH] Mint event detected:", idStr);

  try {
    // Load the NFT metadata
    const nft = await fetchAndCacheMetadata(idStr, contract, false);
    if (!nft) {
      console.warn("[FRESH] No metadata returned for token", idStr);
      return;
    }

    const now = Date.now();
    const cutoff = now - 24 * 60 * 60 * 1000; // 24h

    // Load previous list
    let list = loadRecentMintsFromCache();
    console.log("[FRESH] BEFORE:", list.length, "recent items");

    // Insert newest first
    list.unshift({
      id: nft.id,
      metadata: nft.metadata,
      timestamp: now
    });

    // Remove older than 24h
    list = list.filter((m) => m.timestamp >= cutoff);

    // Keep only last 20
    if (list.length > 20) {
      list = list.slice(0, 20);
    }

    // Save + update UI
    saveRecentMintsToCache(list);
    setRecentMints(list);

    console.log("[FRESH] AFTER:", list.length, "recent items");
  } catch (err) {
    console.error("[FRESH] Mint event handler FAILED:", err);
  }
};

// =========================================================
// HOOK UP EVENT LISTENER — RUNS ONCE (NO SPAM)
// =========================================================
useEffect(() => {
  if (!contract) return;

  console.log("[FRESH] Installing Transfer listener…");

  // Load cached recent mints on startup so UI isn’t empty
  const cached = loadRecentMintsFromCache();
  console.log("[FRESH] Startup cache load:", cached.length, "items");
  setRecentMints(cached);

  // ERC721 mint = Transfer(from = 0x0, to = minter, tokenId)
  const filter = contract.filters.Transfer(null, null);

  const onTransfer = (from, to, tokenId) => {
    const idStr = tokenId.toString();

    console.log("[EVENT] Transfer fired:", { from, to, id: idStr });

    // Detect mint
    if (from === ethers.constants.AddressZero) {
      console.log("[EVENT] MINT detected:", idStr);

      // Update mint count (ONE call, no spam)
      loadMintCount();

      // Update Fresh Off the Grill list
      handleMintEvent(tokenId);

      // Refresh wallet NFTs once
      loadMyMcRibsFast(1);
    }
  };

  contract.on(filter, onTransfer);

  return () => {
    console.log("[FRESH] Removing Transfer listener");
    contract.off(filter, onTransfer);
  };
}, [contract]);


  // ====================================================================
  // HELPER FUNCTION: Handles the fetch from the contract and updates the cache.
  // ====================================================================
  const fetchAndCacheMetadata = async (id, contract, isBackgroundUpdate) => {
    try {
      const uri = await contract.tokenURI(id);
      const metadata = parseTokenURI(uri, id.toString());
      const nftData = { id, metadata };
      localStorage.setItem(`mcrib_nft_${id}`, JSON.stringify(nftData));
      return nftData;
    } catch (e) {
      console.error(`Failed to fetch/cache metadata for #${id}:`, e);
      return null;
    }
  };

  // ====================================================================
  // NEW DEDICATED FUNCTION: Loads the raw list of owned token IDs from the contract.
  // ====================================================================
  const loadOwnedTokenIDs = async () => {
    if (!account || !contract) return [];
    try {
      const balance = await contract.balanceOf(account);
      const tokenIds = [];
      for (let i = 0; i < balance.toNumber(); i++) {
        const id = await contract.tokenOfOwnerByIndex(account, i);
        tokenIds.push(id.toNumber());
      }
      setAllTokenIDs(tokenIds);
      return tokenIds;
    } catch (e) {
      console.error("Failed to load owned token IDs:", e);
      return [];
    }
  };

  // ====================================================================
  // INSTANT LOAD — 25 NFTs WITH 1 RPC CALL USING MULTICALL
  // ====================================================================
  const loadMyMcRibsFast = async (page = 1) => {
    if (!account || !contract) return;

    setMintStep(`Loading page ${page}…`);

    let tokenIds = allTokenIDs;
    if (tokenIds.length === 0) {
      tokenIds = await loadOwnedTokenIDs();
      if (tokenIds.length === 0) {
        setMintStep("No McRibs found");
        return;
      }
    }

    const start = (page - 1) * NFTS_PER_PAGE;
    const end = start + NFTS_PER_PAGE;
    const idsForPage = tokenIds.slice(start, end);
    if (idsForPage.length === 0) {
      setMyNFTs([]);
      setMintStep(null);
      return;
    }

    const multicall = new ethers.Contract(MULTICALL_ADDRESS, MULTICALL_ABI, provider);
    const calls = idsForPage.map(id => ({
      target: CONTRACT_ADDRESS,
      callData: contract.interface.encodeFunctionData("tokenURI", [id])
    }));

    try {
      const [, returnData] = await multicall.aggregate(calls);

      const loaded = [];
      for (let i = 0; i < idsForPage.length; i++) {
        const rawUri = contract.interface.decodeFunctionResult("tokenURI", returnData[i])[0];
        const metadata = parseTokenURI(rawUri, idsForPage[i].toString());
        const fresh = { id: idsForPage[i].toString(), metadata };
        localStorage.setItem(`mcrib_nft_${idsForPage[i]}`, JSON.stringify(fresh));
        loaded.push(fresh);
      }

      setMyNFTs(loaded);
      setCurrentPage(page);
      setMintStep(null);
    } catch (err) {
      console.error("Multicall failed, falling back...", err);
      // Fallback to old method if needed
      const loaded = [];
      for (const id of idsForPage) {
        const fresh = await fetchAndCacheMetadata(id, contract, false);
        if (fresh) loaded.push(fresh);
      }
      setMyNFTs(loaded);
      setCurrentPage(page);
      setMintStep(null);
    }
  };

  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  // UNIVERSAL tokenURI PARSER — WORKS EVERY TIME
  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  // FORCE PRE-REVEAL SCREEN — bypasses all parsing issues
  const forcePreRevealImage = (tokenId) => {
    return `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiMwMDAwMDAiLz48dGV4dCB4PSIxNiIgeT0iOSIgZm9udC1mYW1pbHk9Im1vbm9zcGFjZSIgZm9udC1zaXplPSIyLjgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNGRjZCMDAiPk5PVyBTRVJWSU5HPC90ZXh0Pjx0ZXh0IHg9IjE2IiB5PSIxNiIgZm9udC1mYW1pbHk9Im1vbm9zcGFjZSIgZm9udC1zaXplPSI1IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjRkZGRkZGIiBzdHJva2U9IiNGRjZCMDAiIHN0cm9rZS13aWR0aD0iMC44Ij5NY1JJQiAje3Rva2VuSWR9PC90ZXh0Pjx0ZXh0IHg9IjE2IiB5PSIyNCIgZm9udC1mYW1pbHk9Im1vbm9zcGFjZSIgZm9udC1zaXplPSIxLjgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM4ODg4ODgiPlJldmVhbHMgaW4gYmxvY2tzPC90ZXh0Pjwvc3ZnPg==`.replace("{{tokenId}}", tokenId);
  };

  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  // FINAL UNIVERSAL tokenURI PARSER — WORKS WITH RAW JSON (current contract)
  // ALSO STILL WORKS IF SOMEONE RETURNS BASE64 (future-proof)
  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  const parseTokenURI = (uri, tokenId) => {
    try {
      let jsonString;

      if (uri.startsWith("{")) {
        jsonString = uri;
      }
      else if (uri.startsWith("data:application/json")) {
        const b64 = uri.split("base64,")[1];
        jsonString = atob(b64);
      }

      const json = JSON.parse(jsonString);

      return {
        name: json.name || `McRib #${tokenId}`,
        description: json.description || "Fresh McRib. Ready to eat!",
        image: json.image,
        attributes: json.attributes || []
      };
    } catch (e) {
      console.error("Metadata parse failed for token", tokenId, e);
      console.log("RAW URI:", uri);

      return {
        name: `McRib #${tokenId}`,
        description: "Loading…",
        image: null,
        attributes: []
      };
    }
  };

const [mintQuantity, setMintQuantity] = useState(1);

const handleMint = async () => {
  if (!account) return alert("Connect wallet first");
  if (mintQuantity < 1 || mintQuantity > 20) {
    return alert("You can only mint 1–20 McRibs at a time");
  }

  setCooking(true);
  setMintStep(`Preparing grill for ${mintQuantity} McRib${mintQuantity > 1 ? "s" : ""}...`);

  try {
    const mcrib = new ethers.Contract(MCRIB_ADDRESS, ERC20_ABI, signer);

    // ============================
    // DEBUG START — BEFORE MINT
    // ============================
    console.log("===== MULTI-MINT DEBUG START =====");
    console.log(`Minting quantity: ${mintQuantity}`);

    const dogeBefore = await provider.getBalance(account);
    const mcribBefore = await mcrib.balanceOf(account);
    console.log("DOGE BEFORE:", ethers.utils.formatEther(dogeBefore));
    console.log("MCRIB BEFORE:", ethers.utils.formatUnits(mcribBefore, 15));

    const dogeFeePer = await contract.dogeFee();
    const mcribPricePer = await contract.mcribPrice();

    const totalDogeRequired = dogeFeePer.mul(mintQuantity);
    const totalMcribRequired = mcribPricePer.mul(mintQuantity);

    console.log("Per mint — DOGE fee:", ethers.utils.formatEther(dogeFeePer));
    console.log("Per mint — MCRIB price:", ethers.utils.formatUnits(mcribPricePer, 15));
    console.log(`TOTAL REQUIRED → DOGE: ${ethers.utils.formatEther(totalDogeRequired)} | MCRIB: ${ethers.utils.formatUnits(totalMcribRequired, 15)}B`);

    // ============================
    // CHECK REMAINING SUPPLY
    // ============================
    setMintStep("Checking remaining supply...");
    const currentSupply = await contract.totalSupply();
    if (currentSupply.add(mintQuantity).gt(10000)) {
      throw new Error(`Only ${10000 - currentSupply.toNumber()} McRibs left — can't mint ${mintQuantity}`);
    }

    // ============================
    // 1. CHECK NATIVE DOGE BALANCE
    // ============================
    setMintStep("Checking DOGE balance...");
    const nativeBalance = await provider.getBalance(account);
    if (nativeBalance.lt(totalDogeRequired)) {
      throw new Error(
        `Not enough DOGE! Need ${ethers.utils.formatEther(totalDogeRequired)} DOGE (${mintQuantity} × ${ethers.utils.formatEther(dogeFeePer)})`
      );
    }

    // ============================
    // LEGACY GAS & SAFE LIMITS (The Fix: Simple & Separate)
    // ============================
    setMintStep("Setting Dynamic Gas Options...");
    
    // --- Dynamic Gas Limit Calculation ---
    // These constants are estimated based on your contract's logic (looping/random ID generation).
    const PER_MINT_COST = 400000; // Estimated gas cost for one mint loop iteration
    const BASE_OVERHEAD = 500000; // Baseline gas cost for function setup
    const MAX_LIMIT = 10000000;   // Hard cap at 10 Million to prevent sending excessive gas

    const calculatedGasLimit = BASE_OVERHEAD + (mintQuantity * PER_MINT_COST);
    
    // Use the calculated limit, or the MAX_LIMIT if the calculation exceeds it (for safety)
    const finalGasLimit = calculatedGasLimit > MAX_LIMIT ? MAX_LIMIT : calculatedGasLimit;

    // Inherit gasPrice from TX_OPTIONS, then add safe gasLimit for each transaction.
    const approveTxOptions = { ...TX_OPTIONS, gasLimit: ethers.BigNumber.from(400000) }; // 400k safe limit for approval
    const mintTxOptions = { 
      ...TX_OPTIONS, 
      gasLimit: ethers.BigNumber.from(finalGasLimit) // <-- NOW DYNAMICALLY SCALED!
    }; 
    // ============================
    // 2. APPROVE MCRIB (if needed)
    // ============================
    setMintStep("Checking MCRIB approval...");
    const allowance = await mcrib.allowance(account, CONTRACT_ADDRESS);
    
    // 🛑 CRITICAL DEBUG LINES 🛑
    console.log("DEBUG: Contract Address Being Checked:", CONTRACT_ADDRESS);
    console.log("DEBUG: Current Allowance:", allowance.toString());
    console.log("DEBUG: Required MCRIB:", totalMcribRequired.toString());
    // 🛑 🛑 🛑 🛑 🛑 🛑 🛑 🛑 🛑 🛑

    if (allowance.lt(totalMcribRequired)) {
      setMintStep(`Approving ${ethers.utils.formatUnits(totalMcribRequired, 15)}B MCRIB...`);
      const approveTx = await mcrib.approve(
        CONTRACT_ADDRESS,
        totalMcribRequired.mul(10), // generous buffer
        approveTxOptions // <-- USES CORRECT, SEPARATE APPROVE OPTIONS
      );
      await approveTx.wait();
      console.log("MCRIB APPROVED FOR", ethers.utils.formatUnits(totalMcribRequired.mul(10), 15) + "B");
    } else {
      console.log("MCRIB already approved");
    }

    // ============================
    // 3. MINT MULTIPLE McRIBS
    // ============================
    setMintStep(`Grilling ${mintQuantity} McRib${mintQuantity > 1 ? "s" : ""}...`);
    console.log("Sending mint tx with value:", ethers.utils.formatEther(totalDogeRequired), "DOGE");

    const mintTx = await contract.mint(mintQuantity, {
      ...mintTxOptions,    // ← USES CORRECT, SEPARATE MINT OPTIONS
      value: totalDogeRequired
    });

    setMintStep("On the blockchain... almost there!");
    const receipt = await mintTx.wait();
    // =========================
    // DROP COOKING SCREEN FAST
    // =========================
    setMintStep("McRibs served fresh!");
    setCooking(false); // <-- this is the important part

    console.log("MINT TX SUCCESS:", receipt.transactionHash);

    // ============================
    // DEBUG END — AFTER MINT
    // ============================
    const dogeAfter = await provider.getBalance(account);
    const mcribAfter = await mcrib.balanceOf(account);

    console.log("DOGE AFTER:", ethers.utils.formatEther(dogeAfter));
    console.log("MCRIB AFTER:", ethers.utils.formatUnits(mcribAfter, 15));
    console.log("DOGE DEDUCTED:", ethers.utils.formatEther(dogeBefore.sub(dogeAfter)));
    console.log("MCRIB DEDUCTED:", ethers.utils.formatUnits(mcribBefore.sub(mcribAfter), 15));
    console.log("===== MULTI-MINT DEBUG END =====");

// ============================
// SUCCESS & REFRESH (FINAL NON-LAG FIX)
// ============================

// Drop the cooking screen FAST so UI doesn't freeze
setMintStep("McRibs served fresh!");
setTimeout(() => {
  setCooking(false);
  setMintStep("");
}, 1200);

// Begin background refresh (non-blocking, zero UI lag)
setTimeout(async () => {

  // Still keep your full refresh cycle — just moved to run AFTER overlay closes.
  // CRITICAL FIX: Moving the slow functions to background prevents the freeze.

  // === REAL-TIME REFRESH CYCLE — WITHOUT loadData() ===
  await loadBalances();        // Update DOGE + MCRIB balance
  await loadOwnedTokenIDs();   // Instantly refresh owned IDs
  await loadMyMcRibsFast(1);   // Reload page 1

}, 1500);

// Fire success alert AFTER the new data has started refreshing
setTimeout(() => {
  alert(`${mintQuantity} McRib${mintQuantity > 1 ? "s" : ""} successfully minted!`);
}, 1800);

} catch (error) {
  console.error("Mint failed:", error);

  // Keep your smart fallback error message
  try {
    const fee = await contract.dogeFee();
    const price = await contract.mcribPrice();
    const totalDoge = fee.mul(mintQuantity);
    const totalMcrib = price.mul(mintQuantity);

    alert(
      `Mint failed — Required for ${mintQuantity}:\n` +
      `${ethers.utils.formatEther(totalDoge)} DOGE + ${ethers.utils.formatUnits(totalMcrib, 15)}B MCRIB\n\n` +
      (error.message || "Check wallet & balances")
    );
  } catch {
    alert("Mint failed — check network, balances, or try again.");
  }
} finally {
  // Ensure cooking state auto-clears no matter what
  setTimeout(() => {
    setCooking(false);
    setMintStep("");
  }, 3000);
}
};

useEffect(() => {
  loadMintCount(); // runs once at startup
}, []);

useEffect(() => {
  if (!account) return;

  loadBalances();
  loadMyMcRibsFast(currentPage);

}, [account, currentPage]);

  return (
    // YOUR ENTIRE BEAUTIFUL JSX — 100% UNTOUCHED
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating McRib Background */}
      <div className="fixed inset-0 bg-floating-mcrib opacity-10" />
      {/* Fire & Smoke Title Effect */}
      <div className="fixed top-0 left-0 right-0 flex justify-center pointer-events-none z-20">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 4 }}
          className="fire-glow"
        >
          <h1 className="text-6xl sm:text-7xl md:text-8xl font-pixel text-mcrib-fire opacity-60 blur-xl select-none">
            McRIB PIXELS
          </h1>
        </motion.div>
      </div>
      {/* Minting Overlay */}
      {cooking && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center gap-6">
          <motion.h1
  animate={{ scale: [1, 1.3, 1] }}
  transition={{ repeat: Infinity, duration: 1.5 }}
  className="text-4xl sm:text-5xl md:text-6xl font-pixel text-mcrib-gold"
>
            ...COOKING...
          </motion.h1>
          <div className="w-96 h-8 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-mcrib-red via-mcrib-fire to-mcrib-gold"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 3, ease: "linear" }}
            />
          </div>
          <p className="text-xl text-mcrib-gold font-pixel">{mintStep}</p>
        </div>
      )}
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="text-center pt-20 relative z-10"
      >
        <h1 className="text-4xl sm:text-6xl md:text-7xl font-pixel text-mcrib-gold drop-shadow-2xl relative">
          McRIB PIXELS
          <div className="smoke-particles" />
        </h1>
        <p className="text-2xl mt-4 text-mcrib-red font-bold">
          50B $MCRIB + 1 DOGE
        </p>
        {/* Balance Display — Shows Native DOGE (MetaMask's DOGE) */}
        {account && (
          <div className="mt-4 text-lg font-pixel">
            <span className="text-mcrib-gold">
              MCRIB: {parseFloat(userMcribBalance).toFixed(2)}B
            </span>{" "}
            |{" "}
            <span className="text-mcrib-gold">
              DOGE: {parseFloat(userDogeBalance).toFixed(3)}
            </span>
          </div>
        )}
        <div className="flex justify-center gap-6 mt-8">
          {account ? (
            <div className="flex gap-4 items-center">
              <span className="text-green-400 font-bold">
                {account.slice(0, 6)}...{account.slice(-4)}
              </span>
              <button
                onClick={disconnectWallet}
                className="bg-gray-700 px-4 py-2 rounded-full text-sm"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={connectWallet}
              className="bg-mcrib-red px-8 py-4 rounded-full text-xl font-bold hover:scale-110 transition"
            >
              CONNECT WALLET
            </button>
          )}
        </div>

        {/* === NEW MULTI-MINT SECTION === */}
        <div className="flex flex-col items-center gap-8 mt-12">
          <div className="flex items-center gap-8">
            {[1, 5, 10, 20].map((num) => (
              <button
                key={num}
                onClick={() => setMintQuantity(num)}
                disabled={cooking}
                className={`px-8 py-4 rounded-full font-bold text-2xl transition-all shadow-lg ${
                  mintQuantity === num
                    ? "bg-mcrib-gold text-black scale-110 shadow-2xl"
                    : "bg-mcrib-red/80 text-white hover:scale-105"
                } disabled:opacity-50`}
              >
                {num === 20 ? "MAX" : num}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-6">
            <input
              type="number"
              min="1"
              max="20"
              value={mintQuantity}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 1;
                if (val >= 1 && val <= 20) setMintQuantity(val);
                else if (val > 20) setMintQuantity(20);
              }}
              className="w-24 md:w-32 px-4 py-3 bg-black/80 border-4 border-mcrib-gold rounded-2xl text-center text-2xl md:text-4xl font-pixel text-mcrib-gold focus:outline-none focus:border-mcrib-fire"
              disabled={cooking}
            />
            <span className="text-3xl font-pixel text-mcrib-gold">McRibs</span>
          </div>

          {account && (
            <div className="text-center">
              <p className="text-2xl font-pixel text-gray-400">Total Cost:</p>
              <p className="text-4xl font-pixel text-mcrib-gold mt-2">
                {(mintQuantity * 1).toFixed(2)} DOGE + {(mintQuantity * 50).toFixed(0)}B $MCRIB
              </p>
              <p className="text-sm text-gray-500 mt-3 font-pixel">
                (Exact price pulled from contract)
              </p>
            </div>
          )}

          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.96 }}
            onClick={handleMint}
            disabled={cooking || !account}
            className="bg-gradient-to-r from-mcrib-fire to-mcrib-gold text-black px-10 py-6 md:px-20 md:py-8 rounded-full text-3xl md:text-5xl font-bold shadow-3xl disabled:opacity-50 disabled:cursor-not-allowed mt-8"
          >
            {cooking ? "...COOKING..." : `MINT ${mintQuantity} McRIB${mintQuantity > 1 ? "S" : ""}!`}
          </motion.button>
        </div>
      </motion.header>
                  {/* Tabs — CLEAN, NO COMMENTS, NO BULLSHIT */}
      <div className="flex justify-center gap-8 md:gap-12 mt-12 text-xl md:text-3xl font-pixel tracking-wider">
        <button
          onClick={() => setActiveTab("recent")}
          className={activeTab === "recent" ? "text-mcrib-gold drop-shadow-lg" : "text-gray-500"}
        >
          FRESH OFF THE GRILL
        </button>
        <button
  onClick={() => {
    setActiveTab("mynfts");
    loadMyMcRibsFast(currentPage);  // ← NOW PASSES THE CURRENT PAGE
  }}
  className={activeTab === "mynfts" ? "text-mcrib-gold drop-shadow-lg" : "text-gray-500"}
>
  MY McRIBS ({allTokenIDs.length})
</button>
      </div>

      {/* Traits Grid — NOW WITH BACKGROUNDS FIRST */}
      <div className="max-w-6xl mx-auto mt-16 px-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {[
          { trait: "Background", options: BACKGROUND_RARITIES },
          ...Object.entries(TRAIT_RARITY).map(([trait, options]) => ({ trait, options }))
        ].map(({ trait, options }) => (
          <div
            key={trait}
            className="bg-black/60 backdrop-blur-md border-2 border-mcrib-gold/50 rounded-2xl p-6"
          >
            <h3 className="text-mcrib-gold font-pixel text-lg uppercase mb-4">
              {trait === "Background" ? "BACKGROUND" : trait.toUpperCase()}
            </h3>
            {Object.entries(options).map(([name, rarity]) => (
              <motion.div
                key={name}
                whileHover={{ scale: 1.05, backgroundColor: "#ff450030" }}
                className="bg-black/50 rounded-lg p-3 text-center border border-mcrib-gold/30 mb-2 cursor-pointer"
                title={`${rarity} rarity`}
              >
                <div className="text-sm">{name}</div>
                <div className="text-xs text-mcrib-gold">
                  {rarity} rarity
                </div>
              </motion.div>
            ))}
          </div>
        ))}
      </div>

{/* ─── THE ELITE ECOSYSTEM HUB (ORIGIN / BRANDING / TECH VISION) ─── */}
      <section className="relative max-w-7xl mx-auto mt-20 md:mt-32 px-4 md:px-6 font-pixel overflow-hidden md:overflow-visible text-white">
        
        {/* 1. THE ORIGIN STORY & OUR MOTTO SECTION */}
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0 items-center mb-20">
          <div className="lg:col-span-7 bg-gradient-to-r from-mcrib-red/40 to-transparent p-1 shadow-2xl rounded-3xl">
            <div className="bg-black/95 p-6 md:p-10 rounded-3xl border-l-8 border-mcrib-fire">
              <h2 className="text-4xl md:text-6xl text-mcrib-gold mb-6 italic tracking-tighter uppercase font-pixel">BORN FROM A BITE</h2>
              <p className="text-lg md:text-xl text-gray-200 leading-relaxed font-sans font-light">
                McRIB Pix didn’t start in a boardroom. It started as a <span className="text-mcrib-fire font-pixel text-xl uppercase italic">"Joke"</span>—a pixelated tribute to a legendary sandwich. 
                What began as a community laugh turned into a <span className="text-white font-bold uppercase italic">"Ride-or-Die"</span> legacy. 
                Built by a Solo Dev and powered by AI partners, we are proof that vision and community outweigh corporate funding.
              </p>
            </div>
          </div>
          
          <div className="lg:col-span-5 ml-0 lg:ml-8 bg-mcrib-gold/10 backdrop-blur-xl border-2 border-mcrib-gold/30 p-6 md:p-8 rounded-2xl lg:transform lg:rotate-2 shadow-2xl z-20">
            <h3 className="text-mcrib-gold text-2xl mb-4 underline uppercase italic font-pixel text-center">Our Motto</h3>
            <p className="text-gray-400 text-lg italic leading-snug font-sans text-center">
              "Building the plane in the air while learning to fly. Our community is forged on X and Telegram—no noise, no distractions, just the flight crew in this digital deli."
            </p>
          </div>
        </div>

        {/* 2. THE BIG BRANDING: RESPONSIVE TEXT SIZE */}
        <div className="relative h-24 md:h-40 w-full flex items-center justify-center z-0 pointer-events-none select-none">
          <h2 className="text-[4rem] md:text-[12rem] lg:text-[18rem] font-black opacity-[0.04] leading-none text-white italic uppercase tracking-tighter text-center">
            MULTI-CHAIN MEAT
          </h2>
        </div>

        {/* 3. THE TECH: THE TECHNICAL VISION BUBBLE */}
        <div className="relative mb-20 md:mb-40 z-10 bg-black/40 border-t-2 border-b-2 border-mcrib-gold/20 py-12 md:py-24 px-6 md:px-10 rounded-[2rem] md:rounded-[4rem] backdrop-blur-sm overflow-hidden">
             <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch relative z-10">
                
                {/* LEFT COLUMN: THE VISION HEADER + UNIT PX + RED BOX */}
                <div className="lg:col-span-6 flex flex-col justify-between relative">
                   
                   {/* "THE VISION" HEADER - SCALE DOWN FOR MOBILE */}
                   <div className="mb-4 text-center lg:text-left">
                     <span className="block text-xl md:text-[3rem] text-mcrib-fire font-pixel italic tracking-widest opacity-80 lg:ml-2">THE</span>
                     <h2 className="text-5xl md:text-7xl lg:text-7xl text-white opacity-10 select-none uppercase tracking-[0.1em] md:tracking-[0.2em] font-black font-pixel -mt-2 md:-mt-4">Vision</h2>
                   </div>
                   
                   {/* UNIT PX - FLUID WIDTH FOR MOBILE */}
                   <div className="relative z-10 mt-6 md:mt-10 flex justify-center overflow-visible">
                    <img 
                      src={process.env.PUBLIC_URL + "/Unit-PX.png"} 
                      className="w-full max-w-sm md:max-w-none h-auto lg:w-[1200px] lg:-ml-25 object-contain drop-shadow-[0_0_80px_rgba(255,107,0,0.3)]" 
                      alt="UNIT-PX" 
                    />
                    <div className="absolute bottom-5 md:bottom-10 left-1/2 -translate-x-1/2 w-1/2 h-8 bg-black/60 blur-3xl rounded-full" />
                  </div>

                  {/* RED BOX - RESPONSIVE FONT & SPACING */}
                  <div className="mt-8 lg:mt-auto mb-[5px] p-6 md:p-8 bg-mcrib-red/10 rounded-3xl border-2 border-mcrib-red/40 italic text-gray-300 text-base md:text-lg text-center relative z-20 shadow-2xl">
                    "We are engineering the metadata to serve as a genetic blueprint. Whether through our own virtual world expansion or collaborative plug-and-play systems, we are clearing the path for these bots to stand tall in 3D arenas regardless of the obstacles."
                  </div>
                </div>

                {/* RIGHT COLUMN: THE NARRATIVE TECH BREAKDOWN */}
                <div className="lg:col-span-6 space-y-10 lg:space-y-12 font-sans text-gray-200 py-6 md:py-10 flex flex-col justify-between">
                  {[
                    { title: "I. The Genesis Spark", color: "border-mcrib-fire", text: "McRIB Pix exists at the intersection of meme-culture and on-chain permanence. By utilizing 10,000 unique SVG data structures stored directly on the Dogechain, we ensured that this project would outlive the platforms it was born on." },
                    { title: "II. Economic Incineration", color: "border-mcrib-gold", text: "The 'Eat' mechanic is the heart of our deflationary engine. Evolving an NFT requires the permanent destruction (burn) of $MCRIB tokens. If the collection reaches full evolution, 50% of the total token supply is removed from the market forever." },
                    { title: "III. The Omni-Vault Protocol", color: "border-white/20", text: "Our 'Lock-and-Sync' vision allows for the secure transit of assets across the blockchain multiverse. Through a sovereign vaulting system, a McRIB is anchored on its origin chain while its utility is instantiated on another." },
                    { title: "IV. The Dimensional Awakening", color: "border-mcrib-red", text: "The endgame is the transition from 2D pixels to high-fidelity 3D entities. With an existing 3D metaverse infrastructure, the goal is to bridge these assets into playable BattleBots." }
                  ].map((item, idx) => (
                    <div key={idx} className={`border-l-4 ${item.color} pl-6 md:pl-8`}>
                      <h3 className="text-2xl md:text-4xl text-mcrib-gold mb-3 md:mb-4 italic uppercase font-pixel tracking-tighter">{item.title}</h3>
                      <p className="text-lg md:text-xl leading-relaxed font-light text-gray-300">{item.text}</p>
                    </div>
                  ))}
                </div>
             </div>
        </div>

        {/* 4. THE ROADMAP: RESPONSIVE GRID */}
        <div className="relative py-10 md:py-20 z-10">
          <h2 className="text-center text-4xl md:text-7xl text-mcrib-gold mb-16 md:mb-24 italic tracking-tighter uppercase font-pixel leading-tight">
            The Slow-Cooked Queue
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 relative">
             <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-mcrib-fire via-mcrib-gold to-transparent opacity-20" />
             
             {[
              { p: "PHASE 1", t: "THE JOKE", s: "DONE", d: "The $MCRIB joke token ignites. Realizing the community wanted a permanent legacy, we pivoted from a temporary laugh to a 10,000-piece on-chain monument." },
              { p: "PHASE 2", t: "THE FOUNDATION", s: "DONE", d: "Deployment of 10,000 100% on-chain SVG assets. We built the 'Eat' mechanic—allowing holders to burn token supply to unlock the 'Fire/Smoke' visual evolution." },
              { p: "PHASE 3", t: "SETTING THE TABLE", s: "LIVE", d: "Launch of the technical vision and goals. We are seeding our 'OG' holders and leveraging AI collaboration to accelerate our solo-dev speed." },
              { p: "PHASE 4", t: "EXPANSION", s: "PENDING", d: "Growing our X and Telegram presence organically. Ensuring secondary markets correctly recognize metadata for proper rarity valuation." },
              { p: "PHASE 5", t: "OMNI-VAULT BLUEPRINTS", s: "PENDING", d: "Researching the 'Lock-and-Sync' protocol for future cross-chain mobility. Internal testing of the secure vault to ensure zero supply dilution." },
              { p: "PHASE 6", t: "KITCHEN RENOVATION", s: "PENDING", d: "Upgrading the site into a full 'Project Hub.' Transitioning to a tabbed UI for real-time roadmap updates and community burn statistics." },
              { p: "PHASE 7", t: "INTERSTELLAR BRIDGE", s: "PENDING", d: "Opening the secure bridge. We will expand to new networks based on community demand, including Solana/SPL research." },
              { p: "PHASE 8", t: "UNIT-PX & BEYOND", s: "PENDING", d: "Introducing the Unit-PX concept on Solana. Bridging pixels into a 3D Metaverse where 'Eaten' status translates into playable characters." },
             ].map((step, i) => (
               <div key={i} className={`p-6 md:p-10 border-2 transition-all duration-700 rounded-3xl relative shadow-2xl bg-black/95
                ${step.s === 'DONE' ? 'border-emerald-500/40 shadow-[0_0_20px_rgba(16,185,129,0.1)]' : step.s === 'LIVE' ? 'border-mcrib-gold shadow-[0_0_30px_rgba(255,204,0,0.2)]' : 'border-gray-800 opacity-80'} 
               `}>
                 <div className={`absolute -top-3 md:-top-4 -left-3 md:-left-4 px-3 md:px-4 py-1 text-[10px] md:text-xs font-bold skew-x-[-12deg] font-pixel
                  ${step.s === 'DONE' ? 'bg-emerald-500 text-black' : step.s === 'LIVE' ? 'bg-mcrib-gold text-black' : 'bg-gray-700 text-gray-400'}`}>
                   {step.p}
                 </div>
                 <div className="flex justify-between items-center mb-4 md:mb-6">
                   <h4 className={`text-xl md:text-3xl tracking-widest italic uppercase font-pixel ${step.s === 'PENDING' ? 'text-gray-600' : 'text-white'}`}>{step.t}</h4>
                   <span className={`text-[10px] md:text-xs font-pixel 
                    ${step.s === 'DONE' ? 'text-emerald-400 font-bold' : step.s === 'LIVE' ? 'text-mcrib-gold animate-pulse font-bold' : 'text-gray-700 opacity-60'}`}>
                      {step.s}
                   </span>
                 </div>
                 <p className={`text-lg md:text-xl font-sans leading-relaxed font-semibold ${step.s === 'PENDING' ? 'text-gray-500' : 'text-gray-300'}`}>
                   {step.d}
                 </p>
               </div>
             ))}
          </div>
        </div>
      </section>

      {/* PERSONAL TRAY — ONLY CONNECTED WALLET'S McRIBS */}
<div className="max-w-7xl mx-auto mt-20 px-4">
        {activeTab === "mynfts" && (
            // CRITICAL FIX: Check the cached list of all IDs for the total count.
            allTokenIDs.length === 0 ? (
                <div className="text-center py-32">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="inline-block"
                    >
                        <div className="bg-black/80 backdrop-blur-xl border-4 border-dashed border-mcrib-gold/60 rounded-3xl px-16 py-24 shadow-2xl">
                            <h2 className="text-4xl md:text-7xl font-pixel text-mcrib-gold mb-6 tracking-wider opacity-90">
                                YOUR TRAY IS EMPTY
                            </h2>
                            <p className="text-3xl text-mcrib-red/90 font-pixel">
                                Mint your first McRib to fill it up
                            </p>
                            <div className="mt-10 text-mcrib-gold/50 font-pixel text-2xl">
                                The grill is hot. The sauce is dripping.
                            </div>
                        </div>
                    </motion.div>
                </div>
            ) : (
                <>
      {/* CLEAN PROFESSIONAL PAGINATION WITH CLICKABLE PAGE BUBBLES */}
      {allTokenIDs.length > NFTS_PER_PAGE && (
        <div className="flex flex-col items-center gap-8 my-16">
          <div className="flex flex-wrap justify-center items-center gap-4">
            {/* Previous Button */}
            <button
              onClick={() => loadMyMcRibsFast(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-8 py-4 bg-mcrib-red/90 hover:bg-mcrib-red disabled:bg-gray-800/60 disabled:cursor-not-allowed rounded-2xl text-white font-pixel text-2xl transition-all shadow-2xl hover:shadow-mcrib-gold/50"
            >
              ← PREV
            </button>

            {/* Page Number Bubbles */}
            {(() => {
              const totalPages = Math.ceil(allTokenIDs.length / NFTS_PER_PAGE);
              const pages = [];

              for (let i = 1; i <= totalPages; i++) {
                // Always show first 3, last 3, current ±2, and smart ellipsis
                if (
                  i === 1 ||
                  i === totalPages ||
                  i === currentPage ||
                  Math.abs(i - currentPage) <= 2 ||
                  (i <= 3 && currentPage <= 5) ||
                  (i >= totalPages - 2 && currentPage >= totalPages - 4)
                ) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => loadMyMcRibsFast(i)}
                      className={`w-16 h-16 rounded-2xl font-pixel text-2xl transition-all shadow-lg border-4 border-mcrib-gold/50 ${
                        currentPage === i
                          ? "bg-mcrib-gold text-black scale-125 shadow-2xl font-bold"
                          : "bg-mcrib-red/90 hover:bg-mcrib-red text-white hover:scale-110"
                      }`}
                    >
                      {i}
                    </button>
                  );
                } 
                // Add ellipsis only once between gaps
                else if (
                  pages.length > 0 &&
                  pages[pages.length - 1] !== "..." &&
                  ((i > 4 && currentPage > 5) || (i < totalPages - 3 && currentPage < totalPages - 4))
                ) {
                  pages.push(<span key={`dots-${i}`} className="text-mcrib-gold text-5xl px-3 select-none">...</span>);
                }
              }
              return pages;
            })()}

            {/* Next Button */}
            <button
              onClick={() => loadMyMcRibsFast(Math.min(Math.ceil(allTokenIDs.length / NFTS_PER_PAGE), currentPage + 1))}
              disabled={currentPage === Math.ceil(allTokenIDs.length / NFTS_PER_PAGE)}
              className="px-8 py-4 bg-mcrib-red/90 hover:bg-mcrib-red disabled:bg-gray-800/60 disabled:cursor-not-allowed rounded-2xl text-white font-pixel text-2xl transition-all shadow-2xl hover:shadow-mcrib-gold/50"
            >
              NEXT →
            </button>
          </div>

          {/* Page Info + Loading Text */}
          <div className="text-center">
            <div className="text-3xl font-pixel text-mcrib-gold drop-shadow-lg">
              Page {currentPage} of {Math.ceil(allTokenIDs.length / NFTS_PER_PAGE)}
            </div>
            <div className="text-xl text-gray-300 mt-2 font-pixel">
              Showing {(currentPage - 1) * NFTS_PER_PAGE + 1}–{Math.min(currentPage * NFTS_PER_PAGE, allTokenIDs.length)} of {allTokenIDs.length} McRibs
            </div>
            {mintStep && (
              <div className="text-3xl font-pixel text-mcrib-gold animate-pulse mt-6">
                {mintStep}
              </div>
            )}
          </div>
        </div>
      )}

                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 justify-center mx-auto">
    {myNFTs // <-- Now we just map over the already-paged array
      .map(({ id, metadata }) => (
    <motion.div
      key={id}
      whileHover={{ y: -50, scale: 1.35 }}
      transition={{ type: "spring", stiffness: 300, damping: 12 }}
      onClick={() => setSelectedNFT({ id, metadata })}
      className="relative group cursor-pointer"
    >
                                <div className="relative overflow-hidden rounded-2xl border-8 border-mcrib-gold shadow-2xl aspect-square w-64 h-64 sm:w-72 sm:h-72 mx-auto">
                                    {/* Lid */}
                                    <div className="absolute top-0 left-0 right-0 h-32 bg-mcrib-red/95 border-t-8 border-x-8 border-mcrib-gold rounded-t-2xl z-20 transition-transform duration-600 group-hover:-translate-y-48" />
                                    {/* McRib — FINAL BULLETPROOF RENDERER */}
                                    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                                        {metadata.image && metadata.image.startsWith("data:image/svg+xml;base64,") ? (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: atob(metadata.image.replace("data:image/svg+xml;base64,", ""))
                                                }}
                                                className="w-full h-full scale-110 group-hover:scale-160 group-hover:translate-y-20 transition-all duration-700"
                                                style={{ pointerEvents: "none" }}
                                            />
                                        ) : (
                                            <img
                                                src={metadata.image}
                                                alt={`McRib #${id}`}
                                                className="w-full h-full object-cover scale-110 group-hover:scale-160 group-hover:translate-y-20 transition-all duration-700"
                                            />
                                        )}
                                    </div>
                                    {/* Label */}
                                    <div className="absolute bottom-6 left-0 right-0 text-center z-30 space-y-3">
                                        <p className="font-pixel text-mcrib-gold text-3xl drop-shadow-2xl bg-black/80 px-8 py-3 rounded-full inline-block">
                                            McRIB #{id}
                                        </p>
                                        {/* EAT BUTTON — ONLY SHOWS IF NOT ALREADY EATEN */}
                                        {metadata.attributes?.some(attr => attr.trait_type === "Status" && attr.value === "Eaten") ? (
                                            <div className="bg-gray-800/90 px-6 py-2 rounded-full inline-block">
                                                <span className="text-gray-500 font-pixel text-lg">EATEN</span>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedNFT({ id, metadata });
                                                }}
                                                className="bg-gradient-to-red hover:bg-red-700 text-white font-pixel text-xl px-8 py-3 rounded-full shadow-lg transform hover:scale-110 transition"
                                            >
                                                EAT THIS McRIB
                                            </button>
                                        )}
                                    </div>
                                    {/* Fire glow */}
                                    <div className="absolute inset-0 bg-mcrib-fire/40 blur-3xl scale-150 group-hover:scale-200 transition-all duration-500 -z-10" />
                                </div>
                            </motion.div>
                        ))}
                    </div>

                </>
            )
        )}
      </div>

      {/* NFT MODAL — TALL BADDIE EDITION */}
      {selectedNFT && (
        <div
  className="fixed inset-0 bg-black/95 z-50 flex items-start justify-center p-4 md:p-12 overflow-y-auto"
          onClick={() => setSelectedNFT(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative bg-black/90 backdrop-blur-xl rounded-3xl border-4 border-mcrib-gold shadow-3xl max-w-6xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button — Top Right */}
            <button
              onClick={() => setSelectedNFT(null)}
              className="absolute top-4 right-4 bg-mcrib-red/90 hover:bg-mcrib-red text-white px-6 py-3 rounded-full font-bold text-lg z-10 shadow-lg hover:scale-110 transition"
            >
              CLOSE ×
            </button>
            <div className="flex flex-col lg:flex-row gap-8 p-8 lg:p-12">
              {/* LEFT: FULL HEIGHT McRIB IMAGE + LONG EAT BUTTON */}
              <div className="flex-1 flex flex-col items-center justify-center min-h-96 lg:min-h-full space-y-6">
                <div className="relative w-full max-w-md">
                  {selectedNFT.metadata.image && selectedNFT.metadata.image.startsWith("data:image/svg+xml;base64,") ? (
                    <div
  dangerouslySetInnerHTML={{
    __html: atob(selectedNFT.metadata.image.replace("data:image/svg+xml;base64,", ""))
  }}
  className="w-full h-auto rounded-3xl shadow-2xl overflow-hidden max-h-[60vh] md:max-h-[80vh]"
/>
                  ) : (
                    <img
                      src={selectedNFT.metadata.image}
                      alt={`McRib #${selectedNFT.id}`}
                      className="w-full h-auto rounded-3xl shadow-2xl"
                      style={{ maxHeight: '80vh', objectFit: 'contain' }}
                    />
                  )}
                </div>
                {/* ONLY ONE EAT BUTTON — UNDER THE IMAGE — THIS IS THE CORRECT ONE */}
                {selectedNFT.metadata.attributes?.some(attr => attr.trait_type === "Status" && attr.value === "Eaten") ? (
                  <div className="w-full max-w-md text-center">
                    <div className="bg-gray-800/90 px-12 py-4 rounded-3xl inline-block">
                      <span className="text-3xl font-pixel text-gray-500">ALREADY EATEN</span>
                    </div>
                  </div>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleEatMcRib(selectedNFT.id)}
                    disabled={isEating}
                    className="w-full max-w-md bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white font-pixel text-4xl py-6 rounded-3xl shadow-2xl disabled:opacity-50"
                  >
                    {isEating ? "EATING..." : "EAT THIS McRIB"}
                  </motion.button>
                )}
              </div>
              {/* RIGHT: TITLE + TRAITS (2 COLUMNS) — NO MORE EAT BUTTON HERE */}
              <div className="flex-1 flex flex-col justify-center space-y-6">
                <div className="text-center lg:text-left">
                  <h2 className="text-5xl md:text-6xl font-pixel text-mcrib-gold drop-shadow-2xl">
                    McRib #{selectedNFT.id}
                  </h2>
                  <p className="text-xl text-mcrib-red/90 mt-2 font-bold">Fresh off the grill</p>
                </div>
                {/* Traits Grid — 2 Columns — CLEAN, NO BUTTONS */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  {selectedNFT.metadata.attributes?.map((attr) => (
                    <div
                      key={attr.trait_type}
                      className="bg-gradient-to-br from-mcrib-gold/20 to-mcrib-fire/10 border-2 border-mcrib-gold/50 rounded-xl p-5 text-center backdrop-blur-sm"
                    >
                      <div className="text-sm text-gray-400 font-pixel uppercase tracking-wider">
                        {attr.trait_type}
                      </div>
                      <div className="text-xl font-bold text-mcrib-gold mt-1">
                        {attr.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <div className="text-center mt-20 pb-20">
        <p className="text-3xl font-pixel text-mcrib-gold">
          {mintedCount} / 10,000 MINTED
        </p>
      </div>
    </div>
  );
}